import { getLogger } from '@omnia/ui/src/lib/services/logger/logger.service'
import type {
    CompletedStandardOrder,
    Film,
    InProgressOrder,
    Journey,
    OrderItemLine,
    OrderPayment,
    OrderShowtime,
    OrderTicketLine,
    Showtime,
    Site,
} from '@vista-digital/ocapi-types/v1'
import type { OrderItemLineSelection } from '@vista-digital/occ-domain'
import {
    getFilmById,
    getJourneyFromCompletedOrder,
    getOrder,
    getOrderItems,
    getOrderPayments,
    getOrderTickets,
    getShowtimeById,
    getSiteById,
    loadCompletedOrderById,
    loadFilmById,
    loadShowtimeById,
    loadSiteById,
    loadTicketPricesForShowtime,
} from '@vista-digital/occ-domain'
import type { OccI18N } from '@vista-digital/occ-ui'
import { useI18n } from '@vista-digital/occ-ui'
import { useUpdateEffect } from '@vista-digital/react-tools/src'
import type { DependencyList } from 'react'
import { useEffect, useState } from 'react'
import { gtm } from '@/content/services/gtm'
import { navigationService } from '@/content/services/navigation.service'
import type { PaymentMethodsPropsApi } from '@/features/orders/common/components/orderPayment/models/paymentMethods.models'

const logger = getLogger('GTM')

/**
 * A hook to handle firing a gtm event only a single time when all required conditions are met
 *
 * @param event Callback function to fire the gtm event
 * @param conditions Array of properties that must be all truthy before firing the event
 * @param resetConditions Array of properties that when changed will reset the event and allow it to fire again
 * @returns A boolean indicating if the event has been fired
 */
export function useGtmEvent(
    event: (i18n: OccI18N) => void,
    conditions?: DependencyList,
    resetConditions?: DependencyList
) {
    const i18n = useI18n()
    const [hasEventFired, setHasEventFired] = useState(false)

    logger.value.trace('useGtmEvent', { conditions, resetConditions, hasEventFired })

    useUpdateEffect(() => {
        logger.value.debug('Reset conditions have changed, resetting gtm event fired state', {
            resetConditions,
            conditions,
        })
        setHasEventFired(false)
    }, [...(resetConditions || [])])

    useEffect(() => {
        if (!i18n) return
        if (hasEventFired) return
        if (conditions?.some(condition => !condition)) return

        logger.value.debug('All conditions met, firing gtm event')

        event(i18n)
        setHasEventFired(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- specific conditions for when gtm event should be called
    }, [...(conditions || []), hasEventFired])

    return hasEventFired
}

export type GtmPaymentEvent = (
    order: InProgressOrder,
    ticketLines: OrderTicketLine[],
    orderItems: OrderItemLineSelection[],
    orderPayments: OrderPayment[],
    film: Film,
    site: Site,
    showtime: Showtime,
    i18n: OccI18N
) => void

const useGtmPayment = (event: GtmPaymentEvent, showtimeId: string, i18n: OccI18N, conditions?: DependencyList) => {
    logger.value.trace('useGtmPayment', { showtimeId })

    const showtime = getShowtimeById(showtimeId)
    const site = getSiteById(showtime?.siteId as string)
    const film = getFilmById(showtime?.filmId as string)

    const tickets = getOrderTickets()
    const orderItems = getOrderItems()
    const orderPayments = getOrderPayments() || []
    const order = getOrder()

    useGtmEvent(
        () => event(order!, tickets, orderItems, orderPayments, film!, site!, showtime!, i18n),
        [...(conditions || []), showtime, site, film, tickets, orderItems, order]
    )
}

export type GtmJourneyEvent = (
    order: CompletedStandardOrder,
    ticketLines: OrderTicketLine[],
    orderItems: OrderItemLine[],
    orderPayments: OrderPayment[],
    film: Film,
    site: Site,
    showtime: Showtime,
    i18n: OccI18N
) => void

const useGtmJourney = (
    event: GtmJourneyEvent,
    orderId: string,
    showtimeId: string,
    i18n: OccI18N,
    conditions?: DependencyList
) => {
    logger.value.trace('useGtmJourney', { orderId, showtimeId })

    const [journey, setJourney] = useState<Journey>()
    const [film, setFilm] = useState<Film>()
    const [site, setSite] = useState<Site>()
    const [showtime, setShowtime] = useState<Showtime>()
    const [ticketPricesLoaded, setTicketPricesLoaded] = useState(false)

    if (!journey) {
        const journeyFromOrder = getJourneyFromCompletedOrder({ orderId })
        if (journeyFromOrder) {
            setJourney(journeyFromOrder)
        } else {
            void loadCompletedOrderById(orderId)
                .then(() => {
                    setJourney(getJourneyFromCompletedOrder({ orderId }))
                })
                .catch(() => {
                    logger.value.warn('Failed to load completed order for GTM tracking', { orderId })
                })
        }
    }

    if (!showtime) {
        void loadShowtimeById(showtimeId).then(() => {
            setShowtime(getShowtimeById(showtimeId))
        })
    }

    const orderFromJourney = journey?.orders?.find(order => order.id === orderId)
    const orderShowtime: OrderShowtime | undefined = orderFromJourney?.showtimes?.find(
        st => st.showtimeId === showtimeId
    )

    if (!film && showtime) {
        void loadFilmById(showtime.filmId).then(() => {
            setFilm(getFilmById(showtime.filmId))
        })
    }

    if (!site && journey) {
        void loadSiteById(journey.siteId).then(() => {
            setSite(getSiteById(journey.siteId))
        })
    }
    const tickets = orderShowtime?.tickets || []
    const orderItems: OrderItemLine[] | undefined = orderFromJourney?.items || []
    const orderPayments = orderFromJourney?.payments || []

    if (!ticketPricesLoaded) {
        void loadTicketPricesForShowtime(showtimeId).then(() => {
            setTicketPricesLoaded(true)
        })
    }

    useGtmEvent(
        () => event(orderFromJourney!, tickets, orderItems, orderPayments, film!, site!, showtime!, i18n),
        [...(conditions || []), showtime, site, film, tickets, orderItems, orderFromJourney, ticketPricesLoaded]
    )
}

export const useGtmBeginSummary = (showtimeId: string, i18n: OccI18N) => {
    logger.value.trace('useGtmBeginSummary', { showtimeId })

    const isPrevPageValid = navigationService.getPreviousPage() !== 'OrderPayment'
    useGtmPayment(gtm.ecommerce.payment.beginCheckout, showtimeId, i18n, [isPrevPageValid])
}
interface UseGtmAddPaymentInfoOptions {
    disabled?: boolean
}

export const useGtmAddPaymentInfo = (showtimeId: string, i18n: OccI18N, options: UseGtmAddPaymentInfoOptions = {}) => {
    const { disabled = false } = options
    logger.value.trace('useGtmAddPaymentInfo', { showtimeId, disabled })

    const isPrevPageValid = !!navigationService.getPreviousPage()
    useGtmPayment(gtm.ecommerce.payment.addPaymentInfo, showtimeId, i18n, [isPrevPageValid, !disabled])
}

export const useGtmConfirmationPayment = (
    orderId: string,
    showtimeId: string,
    paymentMethods: PaymentMethodsPropsApi[] | undefined,
    i18n: OccI18N
) => {
    logger.value.trace('useGtmAddPaymentInfo', { orderId, showtimeId })
    useGtmJourney(gtm.ecommerce.payment.payment, orderId, showtimeId, i18n)
}
